import { ChainId } from '@pancakeswap/chains'
import { nutsProfileABI } from 'config/abi/pancakeProfile'
import { API_PROFILE } from 'config/constants/endpoints'
import { BigNumber } from 'ethers'
import { getNftApi, getRealTokenIdFromId } from 'state/nftMarket/helpers'
import { NftToken } from 'state/nftMarket/types'
import { getTeam } from 'state/teams/helpers'
import { Profile } from 'state/types'
import { getNutsAddress, getNutsProfileAddress } from 'utils/addressHelpers'
import { publicClient } from 'utils/wagmi'
import { erc404ABI } from 'config/abi/erc404'

import { Address } from 'wagmi'
import { bigint } from 'zod'

export interface GetProfileResponse {
  hasRegistered: boolean
  profile?: Profile
}

const transformProfileResponse = (profileResponse): Partial<Profile> => {
  const { 0: userId, 1: numberPoints, 2: teamId, 3: collectionAddress, 4: tokenId, 5: isActive } = profileResponse

  return {
    userId: Number(userId),
    points: Number(numberPoints),
    teamId: Number(teamId),
    tokenId: Number(tokenId),
    collectionAddress,
    isActive,
  }
}

export const getUsername = async (address: string): Promise<string> => {
  try {
    const response = await fetch(`${API_PROFILE}/api/users/${address.toLowerCase()}`)

    if (!response.ok) {
      return ''
    }

    const { username = '' } = await response.json()

    return username
  } catch (error) {
    return ''
  }
}

export const getProfile = async (address: string): Promise<GetProfileResponse | null> => {
  try {
    const client = publicClient({ chainId: ChainId.PULSECHAIN })

    const profileCallsResult = await client.multicall({
      contracts: [
        {
          address: getNutsProfileAddress(),
          abi: nutsProfileABI,
          functionName: 'hasRegistered',
          args: [address as Address],
        },
        {
          address: getNutsProfileAddress(),
          abi: nutsProfileABI,
          functionName: 'getUserProfile',
          args: [address as Address],
        },
      ],
    })

    const [{ result: hasRegistered }, { result: profileResponse }] = profileCallsResult
    if (!hasRegistered) {
      return { hasRegistered: Boolean(hasRegistered), profile: undefined }
    }

    const { userId, points, teamId, tokenId, collectionAddress, isActive } = transformProfileResponse(profileResponse)

    // we need to the the token index to get the real token id
    const tokenIndexCallResult = await client.multicall({
      contracts: [
        {
          address: getNutsAddress(),
          abi: erc404ABI,
          functionName: 'GetIndex',
          args: [BigInt(tokenId || 0)],
        },
      ],
    })
    const [{ result: tokenIndexResult }] = tokenIndexCallResult
    const tokenIndex = tokenIndexResult || 0

    const [team, username, nftRes] = await Promise.all([
      teamId ? getTeam(teamId) : Promise.resolve(null),
      getUsername(address),
      isActive && collectionAddress && tokenIndex
        ? getNftApi(collectionAddress, tokenIndex.toString())
        : Promise.resolve(null),
    ])
    let nftToken: NftToken | undefined

    // If the profile is not active the tokenId returns 0, which is still a valid token id
    // so only fetch the nft data if active

    if (nftRes && collectionAddress) {
      nftToken = {
        tokenId: tokenIndex.toString(),
        realTokenId: tokenId?.toString(),
        name: nftRes.name,
        collectionName: nftRes.collection.name,
        collectionAddress,
        description: nftRes.description,
        attributes: nftRes.attributes,
        createdAt: nftRes.createdAt,
        updatedAt: nftRes.updatedAt,
        image: {
          original: nftRes.image?.original,
          thumbnail: nftRes.image?.thumbnail,
        },
      }
    } else {
      nftToken = undefined
    }

    const profile = {
      userId,
      points,
      teamId,
      tokenId,
      username,
      collectionAddress,
      isActive,
      nft: nftToken,
      team,
      realTokenId: Number(tokenId),
    } as Profile

    return { hasRegistered, profile }
  } catch (e) {
    console.error(e)
    return null
  }
}
